/**
 * @author <ziad.khoury.hanna@oceandatalab.com>
 * @flow
 */
import {html} from 'htm/preact';
import {useState, useEffect, useRef} from 'preact/hooks';
import {formatDate} from '../utils.js';
/*:: import type {Seashot} from '../api.js'; */
import {refreshSeashotPreviewWithId} from '../api.js';

/*:: type Props = {
  +seashot: Seashot,
  +size: number,
  +playing?: boolean,
}; */

function usePreviewStatus(seashot) {
  const [retries, setRetries] = useState /*:: <number> */(0);
  const {id, previewStatus} = seashot;

  useEffect(() => {
    setRetries(0);
  }, [seashot, previewStatus]);
  useEffect(() => {
    switch (previewStatus) {
      case 'queued':
      case 'processing':
        break;
      case 'processed':
      case 'failed':
      case 'timedout':
      default:
        return undefined;
    }

    const timeoutId = setTimeout(async() => {
      await refreshSeashotPreviewWithId(id);
      setRetries(retries + 1);
    }, 5000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [retries, previewStatus]);

  return previewStatus;
}
function useVideo(seashot, playing, props) {
  const {id, previewStatus, previewType} = seashot;
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      if (playing) {
        video.play();
      } else {
        video.pause();
        video.currentTime = 0;
      }
    }
  }, [id, previewStatus, previewType, playing]);

  return previewStatus === 'processed' && previewType === 'video'
    ? html`<video
        ref=${videoRef}
        mute
        loop
        playsinline
        autoplay=${playing}
        ...${props}
      />`
    : null;
}

export function SeashotPreview({seashot, size, playing} /*: Props */) /*: React$Node */ {
  const {id, previewType, startDate, endDate} = seashot;
  const previewStatus = usePreviewStatus(seashot);
  const video = useVideo(seashot, playing, {
    controls: true,
    poster  : `/s/${id}/thumb_2x.jpg`,
    src     : `/s/${id}/thumb_2x.mp4`,
  });

  if (previewStatus !== 'processed') {
    return html`<div className="preview -${previewStatus}" />`;
  }

  const className = `preview -${previewStatus} -${previewType}`;
  const thumb = html`<img
    src="/s/${id}/thumb_2x.jpg"
    alt="Snapshot of the seashot"
  />`;
  const date =
    startDate.getTime() === endDate.getTime()
      ? html`
          <time
            className="represented-date"
            datetime=${startDate.toISOString()}
          >
            ${formatDate(startDate, 'short')}
          </time>
        `
      : html`
          <span className="represented-date">
            <time datetime=${startDate.toISOString()}>
              ${formatDate(startDate, 'short')}
            </time>
            ${' - '}
            <time datetime=${endDate.toISOString()}>
              ${formatDate(endDate, 'short')}
            </time>
          </span>
        `;

  return html`<div className=${className}>${thumb}${video}${date}</div>`;
}

export function SeashotPreviewThumb({
  seashot,
  size,
  playing,
} /*: Props */) /*: React$Node */ {
  const {id, previewType, startDate, endDate} = seashot;
  const previewStatus = usePreviewStatus(seashot);
  const video = useVideo(seashot, playing, {
    preload: 'metadata',
    poster : `/s/${id}/thumb_small.jpg`,
    src    : `/s/${id}/thumb_small.mp4`,
  });

  if (previewStatus !== 'processed') {
    return html`<div className="preview -thumb -${previewStatus}" />`;
  }

  const className = `preview -thumb -${previewStatus} -${previewType}`;
  const thumb = html`<img
    src="/s/${id}/thumb_small.jpg"
    alt="Snapshot of the seashot"
  />`;
  const date =
    startDate.getTime() === endDate.getTime()
      ? html`
          <time
            className="represented-date"
            datetime=${startDate.toISOString()}
          >
            ${formatDate(startDate, 'short')}
          </time>
        `
      : html`
          <span className="represented-date">
            <time datetime=${startDate.toISOString()}>
              ${formatDate(startDate, 'short')}
            </time>
            ${' - '}
            <time datetime=${endDate.toISOString()}>
              ${formatDate(endDate, 'short')}
            </time>
          </span>
        `;

  return html`<div className=${className}>${thumb}${video}${date}</div>`;
}

export function SeashotPreviewPlaceholder() /*: React$Node */ {
  return html`<div className="preview -placeholder" />`;
}
